"use client"

import { css } from "@emotion/css"
import { isEqual } from "lodash"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import type {
  DialogResponseMessage,
  ExtendedIframeState,
  MessageFromIframe,
  SetLanguageMessage,
  SetStateMessage,
} from "@/shared-module/exercise-protocol/core/exercise-service-protocol-types"
import {
  isDownloadFileMessage,
  isHeightChangedMessage,
  isMessageFromIframe,
  isOpenDialogMessage,
  isOpenLinkMessage,
  isRequestIframeReloadMessage,
} from "@/shared-module/exercise-protocol/core/exercise-service-protocol-types.guard"

import type { ExerciseDialogApi } from "./exerciseDialogApi"
import useEventCallback from "./useEventCallback"
import useIframeLinkRequests from "./useIframeLinkRequests"
import useMessageChannel from "./useMessageChannel"

// Inlined from shared-module's BreakFromCentered component: only this prop type is needed
// here, and we don't want this package to depend on a generic layout component.
interface NoSidebar {
  sidebar: false
}
interface WithSidebar {
  sidebar: true
  sidebarPosition: "left" | "right"
  sidebarWidth: string
  sidebarThreshold?: string
}
type BreakFromCenteredProps = NoSidebar | WithSidebar

export type { ExerciseDialogApi }

interface MessageChannelIFrameProps {
  url: string
  postThisStateToIFrame: ExtendedIframeState | null
  onMessageFromIframe: (message: MessageFromIframe, responsePort: MessagePort) => void
  breakFromCenteredProps?: BreakFromCenteredProps
  title: string
  showBorders?: boolean
  disableSandbox?: boolean
  headingBeforeIframe?: string
  onReady?: () => void
  /**
   * Host-provided dialog controller used to answer the iframe's `open-dialog` requests and to confirm
   * its `open-link` / `download-file` requests. Every host that renders this component mounts a
   * dialog system; pass its `useDialog()` result here.
   */
  dialog: ExerciseDialogApi
  /**
   * Names the file a `download-file` request saves as, replacing whatever the iframe suggested. The
   * URL arrives as the host parsed it, so a lookup keyed by the URLs the host handed the iframe has
   * to match that form.
   *
   * Set this where the iframe's suggestion must not be shown to the viewer: the host cannot tell a
   * name a plugin anonymized from one it copied off the uploaded file, so the suggestion is then
   * never used. Leave it unset to keep it, which is what someone looking at their own files wants.
   */
  overrideDownloadFilename?: (url: string) => string
}

const MESSAGE_CHANNEL_IFRAME_TEST_ID = "message-channel-iframe"

/**
 * Sandbox flags for exercise iframes. We include `allow-same-origin` so the embedded
 * document has a real origin (not opaque); that enables normal HTTP cache behavior for
 * the exercise origin. Without it, the iframe is an opaque origin, which is much
 * cache-unfriendly for the embedded app and its assets.
 *
 * MDN's `<iframe>` documentation warns that `allow-scripts` + `allow-same-origin`
 * together is dangerous when the embedded page is **same-origin** with the parent,
 * because sandboxed scripts can then escape the sandbox. That does not apply when
 * the embed's origin is **different** from the host: the browser's same-origin policy
 * still blocks cross-origin scripts from accessing the parent page’s DOM.
 */
const useIframeSandboxingAttribute = (disableSandbox: boolean) => {
  if (disableSandbox) {
    return undefined
  }
  // oxlint-disable-next-line i18next/no-literal-string
  return "allow-scripts allow-forms allow-downloads allow-same-origin"
}

const MessageChannelIFrame: React.FC<React.PropsWithChildren<MessageChannelIFrameProps>> = ({
  url,
  postThisStateToIFrame,
  onMessageFromIframe,
  headingBeforeIframe,
  title,
  showBorders = false,
  disableSandbox = false,
  onReady,
  dialog,
  overrideDownloadFilename,
}) => {
  const { t, i18n } = useTranslation()
  const { openLinkOnRequest, downloadFileOnRequest } = useIframeLinkRequests(
    dialog,
    overrideDownloadFilename,
  )
  const language = i18n.language
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const iframeSandboxAttribute = useIframeSandboxingAttribute(disableSandbox)
  const iframeSrcOrigin = useMemo(() => {
    try {
      return new URL(url, window.location.href).origin
    } catch {
      return null
    }
  }, [url])
  const hasSignaledReadyRef = useRef(false)
  const lastThingPostedRef = useRef<unknown>(null)

  const [reloadNonce, setReloadNonce] = useState(0)
  const [reloadExhausted, setReloadExhausted] = useState(false)

  const [messageChannel, recreateMessageChannel] = useMessageChannel()
  const portSentRef = useRef(false)
  const portSentTimestampRef = useRef<number | null>(null)
  const recoveryAttemptsRef = useRef(0)
  const readyMessageQueueRef = useRef<MessageEvent[]>([])
  const reloadAttemptsRef = useRef(0)
  const reloadTimeoutRef = useRef<number | null>(null)

  const MAX_RECOVERY_ATTEMPTS = 3
  const RECOVERY_TIMEOUT_MS = 5000
  const MAX_RELOAD_ATTEMPTS = 4
  const INITIAL_RELOAD_DELAY_MS = 250

  const resetIframeConnectionState = useCallback(
    ({
      recreateChannel = false,
      resetRecoveryAttempts = true,
    }: {
      recreateChannel?: boolean
      resetRecoveryAttempts?: boolean
    } = {}) => {
      portSentRef.current = false
      portSentTimestampRef.current = null
      hasSignaledReadyRef.current = false
      readyMessageQueueRef.current = []
      if (iframeRef.current) {
        // oxlint-disable-next-line i18next/no-literal-string
        iframeRef.current.dataset.stateSent = "false"
        iframeRef.current.dataset.iframeHeight = "0"
      }
      if (resetRecoveryAttempts) {
        recoveryAttemptsRef.current = 0
      }
      lastThingPostedRef.current = null
      if (recreateChannel) {
        recreateMessageChannel()
      }
    },
    [recreateMessageChannel],
  )

  useEffect(() => {
    const clearReloadTimeout = () => {
      if (reloadTimeoutRef.current !== null) {
        clearTimeout(reloadTimeoutRef.current)
        reloadTimeoutRef.current = null
      }
    }

    resetIframeConnectionState()
    reloadAttemptsRef.current = 0
    clearReloadTimeout()
    setReloadNonce(0)
    setReloadExhausted(false)

    return clearReloadTimeout
  }, [resetIframeConnectionState, url])

  const scheduleIframeReload = useCallback(() => {
    if (!iframeRef.current) {
      return
    }
    if (reloadTimeoutRef.current !== null) {
      console.info(
        "[MessageChannelIFrame] Ignoring duplicate iframe reload request while a reload is already scheduled",
      )
      return
    }
    if (reloadAttemptsRef.current >= MAX_RELOAD_ATTEMPTS) {
      console.error(
        `[MessageChannelIFrame] Max iframe reload attempts (${MAX_RELOAD_ATTEMPTS}) reached. Giving up.`,
      )
      setReloadExhausted(true)
      return
    }

    const attempt = reloadAttemptsRef.current + 1
    const delay = Math.min(INITIAL_RELOAD_DELAY_MS * 2 ** (attempt - 1), 2000)
    console.warn(
      `[MessageChannelIFrame] Scheduling iframe reload (attempt ${attempt}/${MAX_RELOAD_ATTEMPTS}) in ${delay}ms`,
    )

    reloadAttemptsRef.current = attempt
    reloadTimeoutRef.current = window.setTimeout(() => {
      reloadTimeoutRef.current = null
      resetIframeConnectionState({ recreateChannel: true })
      setReloadNonce((prev) => prev + 1)
    }, delay)
  }, [resetIframeConnectionState])

  const sendPortToIframe = useCallback((channel: MessageChannel, isRecovery = false) => {
    if (portSentRef.current && !isRecovery) {
      return
    }

    if (!iframeRef.current) {
      return
    }

    const contentWindow = iframeRef.current.contentWindow
    if (!contentWindow) {
      return
    }

    // oxlint-disable-next-line i18next/no-literal-string
    const recoveryMessage = isRecovery ? " (recovery attempt)" : ""
    console.info(`[MessageChannelIFrame] Parent posting message port to iframe${recoveryMessage}`)
    try {
      // The iframe will use port 2 for communication
      contentWindow.postMessage("communication-port", "*", [channel.port2])
      portSentRef.current = true
      portSentTimestampRef.current = Date.now()
    } catch (e) {
      console.error("[MessageChannelIFrame] Posting communication port to iframe failed", e)
    }
  }, [])

  // An exercise inside the iframe can ask us (the parent) to show a dialog and report back the
  // user's choice, and to open a link or download a file on its behalf. All three go through the
  // host-provided `dialog` controller (backed by the app's DialogProvider), which queues concurrent
  // dialogs and handles layering; the link/download requests are confirmed by the user first and get
  // no reply, so nothing in the iframe waits on them.
  const handlePortMessage = useEventCallback(
    (message: WindowEventMap["message"], currentMessageChannel: MessageChannel) => {
      const data = message?.data
      if (data?.message) {
        console.groupCollapsed(`Parent page: received message ${data.message} from iframe`)
      } else {
        console.groupCollapsed(`Parent page: received message from iframe`)
      }

      console.info(JSON.stringify(data, undefined, 2))
      if (isHeightChangedMessage(data)) {
        if (!iframeRef.current) {
          console.error("Cannot send data to iframe because reference does not exist.")
          return
        }
        console.info("Updating height")
        // oxlint-disable-next-line i18next/no-literal-string
        iframeRef.current.height = Number(data.data).toString() + "px"
        iframeRef.current.dataset.iframeHeight = Number(data.data).toString()
      } else if (isOpenLinkMessage(data)) {
        console.info(`The iframe wants to open a link: ${data.data}`)
        openLinkOnRequest(data.data)
      } else if (isDownloadFileMessage(data)) {
        console.info(`The iframe wants to download a file: ${data.url}`)
        downloadFileOnRequest(data.url, data.filename)
      } else if (isRequestIframeReloadMessage(data)) {
        scheduleIframeReload()
      } else if (isOpenDialogMessage(data)) {
        const responsePort = currentMessageChannel.port1
        const {
          requestId,
          dialogType,
          title: dialogTitle,
          body,
          confirmButtonLabel,
          cancelButtonLabel,
        } = data
        const respond = (confirmed: boolean) => {
          const response: DialogResponseMessage = {
            // oxlint-disable-next-line i18next/no-literal-string
            message: "dialog-response",
            requestId,
            confirmed,
          }
          // oxlint-disable-next-line unicorn/require-post-message-target-origin -- postMessage has no targetOrigin param
          responsePort.postMessage(response)
        }
        const dialogBody = (
          <div
            className={css`
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            `}
          >
            {body.map((paragraph, i) => (
              <p
                key={i}
                className={css`
                  margin: 0;
                  white-space: pre-wrap;
                `}
              >
                {paragraph}
              </p>
            ))}
          </div>
        )
        if (dialogType === "confirm") {
          void dialog
            .confirm({
              title: dialogTitle,
              message: dialogBody,
              ...(confirmButtonLabel !== null && confirmButtonLabel !== undefined
                ? { confirmLabel: confirmButtonLabel }
                : {}),
              ...(cancelButtonLabel !== null && cancelButtonLabel !== undefined
                ? { cancelLabel: cancelButtonLabel }
                : {}),
            })
            .then(respond)
        } else {
          void dialog
            .alert({
              title: dialogTitle,
              message: dialogBody,
              ...(confirmButtonLabel !== null && confirmButtonLabel !== undefined
                ? { acknowledgeLabel: confirmButtonLabel }
                : {}),
            })
            .then(() => respond(true))
        }
      } else if (isMessageFromIframe(data)) {
        if (!hasSignaledReadyRef.current) {
          hasSignaledReadyRef.current = true
          reloadAttemptsRef.current = 0
          if (reloadTimeoutRef.current !== null) {
            clearTimeout(reloadTimeoutRef.current)
            reloadTimeoutRef.current = null
          }
          onReady?.()
        }
        try {
          onMessageFromIframe(data, currentMessageChannel.port1)
        } catch (e) {
          console.error("onMessageFromIframe crashed", e)
        }
      } else {
        console.warn("unsupported message")
      }
      console.groupEnd()
    },
  )

  useEffect(() => {
    if (!messageChannel) {
      return
    }
    // We use port 1 for communication, defining a event handler
    // oxlint-disable-next-line unicorn/prefer-add-event-listener -- intentional property-handler
    messageChannel.port1.onmessage = (message: WindowEventMap["message"]) => {
      handlePortMessage(message, messageChannel)
    }

    const queuedMessages = readyMessageQueueRef.current
    if (queuedMessages.length > 0) {
      readyMessageQueueRef.current = []
      queuedMessages.forEach((e) => {
        if (e.data === "ready" && !portSentRef.current) {
          sendPortToIframe(messageChannel)
        }
      })
    }

    return () => {
      // oxlint-disable-next-line unicorn/prefer-add-event-listener -- handler cleanup; `= null` has no addEventListener form
      messageChannel.port1.onmessage = null
    }
  }, [handlePortMessage, messageChannel, sendPortToIframe])

  const handleInitialReadyMessage = useEventCallback((e: WindowEventMap["message"]) => {
    // Verify the source. With sandbox + allow-same-origin, `e.origin` is the iframe
    // document's origin (same as `src` URL). Opaque `"null"` is still accepted for
    // tests and older behavior. Same-origin embeds also match `window.location.origin`.
    const originAllowed =
      disableSandbox ||
      e.origin === "null" ||
      e.origin === window.location.origin ||
      (iframeSrcOrigin !== null && e.origin === iframeSrcOrigin)
    if (!originAllowed || e.source !== iframeRef.current?.contentWindow) {
      if (e.data === "ready") {
        console.warn("[MessageChannelIFrame] Received ready message from invalid origin", {
          origin: e.origin,
          expectedOrigins: ["null", window.location.origin, iframeSrcOrigin].filter(
            (o): o is string => o !== null && o !== "",
          ),
          isSandboxed: !disableSandbox,
        })
      }
      return
    }

    if (e.data !== "ready") {
      return
    }

    if (portSentRef.current && portSentTimestampRef.current) {
      const timeSinceSent = Date.now() - portSentTimestampRef.current
      if (timeSinceSent > RECOVERY_TIMEOUT_MS) {
        if (recoveryAttemptsRef.current < MAX_RECOVERY_ATTEMPTS) {
          console.warn(
            `[MessageChannelIFrame] Received ready message ${timeSinceSent}ms after sending port. Initiating recovery (attempt ${recoveryAttemptsRef.current + 1}/${MAX_RECOVERY_ATTEMPTS})`,
          )
          recoveryAttemptsRef.current += 1

          resetIframeConnectionState({ resetRecoveryAttempts: false })
          readyMessageQueueRef.current.push(e)
          recreateMessageChannel()
          return
        }
        console.error(
          `[MessageChannelIFrame] Max recovery attempts (${MAX_RECOVERY_ATTEMPTS}) reached. Giving up.`,
        )
      }
      return
    }

    if (messageChannel) {
      sendPortToIframe(messageChannel)
    } else {
      readyMessageQueueRef.current.push(e)
    }
  })

  // Set up a temporary listener for the initial ready event
  useEffect(() => {
    const temporaryEventHandler = (e: WindowEventMap["message"]) => {
      handleInitialReadyMessage(e)
    }

    addEventListener("message", temporaryEventHandler)
    return () => {
      removeEventListener("message", temporaryEventHandler)
    }
  }, [handleInitialReadyMessage])

  // Keep the iframe informed of the current user interface language
  useEffect(() => {
    if (!messageChannel) {
      return
    }
    const message: SetLanguageMessage = {
      message: "set-language",
      data: language,
    }
    console.groupCollapsed(`Parent posting set-language message to iframe (${language})`)
    console.info(JSON.stringify(message, undefined, 2))
    console.groupEnd()
    messageChannel.port1.postMessage(message)
  }, [language, messageChannel])

  useEffect(() => {
    if (
      !postThisStateToIFrame ||
      !messageChannel ||
      isEqual(lastThingPostedRef.current, postThisStateToIFrame)
    ) {
      return
    }
    const postData: SetStateMessage = {
      ...postThisStateToIFrame,
      message: "set-state",
    }

    console.groupCollapsed(`Parent posting set-state message to iframe`)
    console.info(JSON.stringify(postData, undefined, 2))
    console.groupEnd()
    messageChannel.port1.postMessage(postData)
    if (iframeRef.current) {
      // oxlint-disable-next-line i18next/no-literal-string
      iframeRef.current.dataset.stateSent = "true"
    }
    lastThingPostedRef.current = postThisStateToIFrame
  }, [messageChannel, postThisStateToIFrame])

  if (!messageChannel) {
    return null
  }

  if (!url || url.trim() === "") {
    return <div>{t("error-cannot-render-dynamic-content-missing-url")}</div>
  }

  return (
    <div
      className={css`
        border: 0;
        /*
          To see the size of the frame in development
          Only top and bottom because frame is 100% of window width.
          */
        ${showBorders && `border: 1px solid black;`}
        margin-bottom: 1rem;
        background-color: #fff;
        padding: 0.8rem 1.25rem;
        border-radius: 0.625rem;
      `}
    >
      {headingBeforeIframe && (
        <h4
          className={css`
            padding-bottom: 0.5rem;
            font-weight: 600;
            font-size: 20px;
          `}
        >
          {headingBeforeIframe}
        </h4>
      )}
      <iframe
        data-testid={MESSAGE_CHANNEL_IFRAME_TEST_ID}
        data-state-sent="false"
        data-iframe-height="0"
        sandbox={iframeSandboxAttribute}
        className={css`
          overflow: hidden;
          width: 100%;
          border: 0;
        `}
        title={title}
        key={reloadNonce}
        ref={iframeRef}
        src={url}
      />
      {reloadExhausted && (
        <div
          className={css`
            margin-top: 0.75rem;
            padding: 0.75rem 1rem;
            border-radius: 0.5rem;
            background-color: #fff3cd;
            color: #664d03;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.75rem;
          `}
        >
          <span>{t("exercise-iframe-reload-exhausted-message")}</span>
          <button
            type="button"
            className={css`
              border: none;
              border-radius: 9999px;
              padding: 0.35rem 0.9rem;
              font-size: 0.9rem;
              font-weight: 500;
              cursor: pointer;
              background-color: #0d6efd;
              color: #fff;
              white-space: nowrap;
            `}
            onClick={() => {
              reloadAttemptsRef.current = 0
              setReloadExhausted(false)
              scheduleIframeReload()
            }}
          >
            {t("exercise-iframe-reload-exhausted-reload-button")}
          </button>
        </div>
      )}
    </div>
  )
}

export default React.memo(MessageChannelIFrame)
